import { useEffect, useMemo, useState } from 'react'
import { RateCard } from '../components/RateCard'
import { RfqForm } from '../components/RfqForm'
import { GATEWAYS, T, useLocale, useT } from '../i18n'
import { localeMeta } from '../i18n/config'
import { isGatewayFor } from '../lib/geo'
import { usePageMeta } from '../lib/meta'
import { type LaneRow, getLanes } from '../lib/rates'

// One generic country money-page, rendered per locale from GATEWAYS + the locale's country.* copy.
export function CountryLandingPage() {
  const t = useT()
  const locale = useLocale()
  const g = GATEWAYS[locale]
  usePageMeta(t('meta.country.title'), t('meta.country.desc'), `/${g?.slug ?? ''}`, [locale])

  const [lanes, setLanes] = useState<LaneRow[]>([])
  useEffect(() => {
    getLanes()
      .then(setLanes)
      .catch(() => {})
  }, [])

  const countryLanes = useMemo(
    () => lanes.filter((l) => isGatewayFor(locale, l.pod_code, l.pod_country)).slice(0, 9),
    [lanes, locale]
  )
  const flag = localeMeta(locale).flag
  const firstPod = g?.podCodes[0]

  return (
    <div>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="hero-glow">
        <div className="mx-auto max-w-6xl px-5 pt-16 pb-10 lg:pt-20">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface-0 px-3 py-1 text-xs text-muted">
            {flag}
            <span className="mono uppercase tracking-wide">{t('country.badge')}</span>
          </span>
          <h1 className="mt-6 max-w-3xl text-[clamp(2.4rem,5.5vw,3.8rem)] font-light leading-[1.05]">
            {t('country.h1')}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted">{t('country.sub')}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <T to="/book" className="rounded-md bg-signal px-4 py-2.5 font-semibold text-on-accent hover:opacity-90">
              {t('nav.getQuote')}
            </T>
            <T
              to="/rate-index"
              className="rounded-md border border-border-strong px-4 py-2.5 font-semibold text-ink hover:border-signal"
            >
              {t('home.ctaIndex')}
            </T>
          </div>
        </div>
      </section>

      {/* ── Native SEO body ──────────────────────────────────────────────── */}
      <section className="mx-auto max-w-3xl px-5 pt-6 pb-4">
        <div className="space-y-4 leading-relaxed text-muted">
          <p>{t('country.body1')}</p>
          <p>{t('country.body2')}</p>
        </div>
      </section>

      {/* ── Country-gateway lanes ────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-5 pt-8 pb-4">
        <div className="flex items-end justify-between">
          <div>
            <div className="mono text-xs uppercase tracking-widest text-signal">{t('country.badge')}</div>
            <h2 className="mt-1 text-2xl font-semibold">{t('country.lanesTitle')}</h2>
          </div>
          <T to="/rates" className="text-sm text-link hover:underline">
            {t('home.allLanes')}
          </T>
        </div>
        {countryLanes.length === 0 ? (
          <div className="card mt-6 p-10 text-center text-muted">{t('home.loadingRates')}</div>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {countryLanes.map((l) => (
              <RateCard key={`${l.pol_code}-${l.pod_code}`} lane={l} />
            ))}
          </div>
        )}
      </section>

      {/* ── In-language RFQ ──────────────────────────────────────────────── */}
      <section className="mx-auto max-w-3xl px-5 pt-10 pb-16">
        <h2 className="font-display text-2xl font-semibold">{t('rfq.title')}</h2>
        <p className="mt-2 text-muted leading-relaxed">{t('rfq.sub')}</p>
        <div className="mt-6 rounded-lg border border-border-strong bg-surface-0 p-6">
          <RfqForm prefill={firstPod ? { pod_code: firstPod } : undefined} />
        </div>
      </section>
    </div>
  )
}
